import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Ring, KPI, SeverityBadge } from "@/components/Primitives";
import { toast } from "sonner";
import { GitCompareArrows, RefreshCw } from "lucide-react";
import { BRAND_SHORT } from "@/lib/brand";

export default function Reconciliation() {
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, f] = await Promise.all([api.get("/reconciliation/summary"), api.get("/reconciliation/findings")]);
    setSummary(s.data); setFindings(f.data);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reconciliation/run"); toast.success(`Reconciliation complete · ${data.findings_count} findings`); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Identity &amp; Asset Reconciliation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Cross-Source Integrity</h1>
          <p className="text-sm text-zinc-500 mt-1">{BRAND_SHORT} assets · {BRAND_SHORT} Agent inventory · Active Directory · Microsoft Entra ID · Microsoft 365</p>
        </div>
        <button onClick={run} disabled={busy} data-testid="recon-run-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><RefreshCw size={14} className={busy ? "animate-spin" : ""}/> Run reconciliation</button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 bg-zinc-950 border border-zinc-900 rounded-md p-6">
          <div className="flex flex-col items-center"><Ring value={summary.asset_coverage_pct} label="Asset Coverage" /></div>
          <div className="flex flex-col items-center"><Ring value={summary.identity_coverage_pct} label="Identity Coverage" /></div>
          <div className="flex flex-col items-center"><Ring value={summary.asset_integrity_score} label="Asset Integrity" /></div>
          <div className="flex flex-col items-center"><Ring value={summary.identity_integrity_score} label="Identity Integrity" /></div>
          <KPI label="Unmapped Devices" value={summary.unmapped_devices || 0} tone="warn" testid="recon-unmapped"/>
          <KPI label="Duplicates" value={summary.duplicates || 0} tone="warn" testid="recon-dupes"/>
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium flex items-center gap-2"><GitCompareArrows size={14}/> Findings ({findings.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-2">Kind</th><th className="text-left px-4 py-2">Severity</th><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Detail</th><th className="text-left px-4 py-2">Detected</th></tr></thead>
          <tbody>
            {findings.map(f => (
              <tr key={f.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{f.kind}</td>
                <td className="px-4 py-2"><SeverityBadge level={f.severity}/></td>
                <td className="px-4 py-2">{f.title}</td>
                <td className="px-4 py-2 text-zinc-400 text-xs">{f.detail}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{f.detected_at?.slice(0,10)}</td>
              </tr>
            ))}
            {findings.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-sm text-zinc-500">No findings. Run reconciliation to detect drift between {BRAND_SHORT}, AD, Entra, and M365.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
